import { useCallback, useEffect, useState } from "react";
import { useIsMounted } from "./useIsMounted";

type Coords = {
  latitude: number;
  longitude: number;
};

type UseLocationResult = {
  coords: Coords | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
};

const getPosition = (options?: PositionOptions): Promise<GeolocationPosition> => {
  return new Promise((resolve: (value: GeolocationPosition) => void, reject: (reason?: any) => void) => {
    if (!navigator || !navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser"));
      return;
    }

    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
};

export const useLocation = (options?: PositionOptions): UseLocationResult => {
  const isMounted = useIsMounted();
  const [coords, setCoords] = useState<Coords | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  const fetchLocation = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const position = await getPosition(options);
      const { latitude, longitude } = position.coords;
      if (isMounted()) {
        setCoords({ latitude, longitude });
      }
    } catch (e: any) {
      const msg = e?.message || "Unable to retrieve location";
      if (isMounted()) {
        setError(msg);
        setCoords(null);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
      }
    }
  }, [isMounted, options]);

  useEffect(() => {
    // fetch on mount and whenever tick changes
    fetchLocation();
  }, [fetchLocation, tick]);

  const refresh = useCallback(() => setTick((t: number) => t + 1), []);

  return { coords, loading, error, refresh };
};

export default useLocation;
